// @ts-nocheck
/**
 * SubregionTable — sortable, LD-grouped table of subregion rollups.
 *
 * Replaces the 11-card grid for users who want to compare subregions head-to-head.
 * The card grid is fine for showing rich detail per subregion, but with 11+ items
 * a tabular layout is much faster to scan because:
 *   1. Numbers are right-aligned in columns, so magnitudes can be eye-compared.
 *   2. Sections are explicitly grouped + labeled by parent LD.
 *   3. Within each LD group, rows sort by the user's chosen metric (default:
 *      score descending, so highest-priority subregions come first).
 *   4. The score column has an inline mini-bar — color = tier, length = score
 *     — so the separate "WARM/HOT/COOL" tier pill is unnecessary.
 *
 * Drilling: whole row click navigates to the subregion detail view (where Map +
 * Route + Walk List actions live). The table itself is for SCANNING, not
 * action — that keeps the resting density low and the click target obvious.
 */
import React, { useMemo, useState } from 'react';
import { ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { LD_NAMES } from '@/lib/computeStats';

// Re-exported so MapView (and other parents) can share the same sort vocab
// without redeclaring the union literally.
export type SubregionSortKey = 'avg_score' | 'total' | 'active_pct' | 'primary_pct';
export type SubregionSortDir = 'asc' | 'desc';

// ---------------------------------------------------------------------------
// Types — shape the parent component's `subregionMap` array provides.
// ---------------------------------------------------------------------------
interface SubregionRow {
  code: string;             // "13-4", "12-1", ...
  ld: number;               // 12 / 13 / 14
  name: string;             // "North Rural"
  total: number;
  active_pct: number;
  primary_pct: number;
  avg_score: number;
  precinct_count: number;
}

interface LDCardLike {
  district_key: string;
  total_voters: number;
  avg_score: number;
}

type SortKey = SubregionSortKey;
type SortDir = SubregionSortDir;

interface Props {
  subregions: SubregionRow[];
  ldCards: LDCardLike[];
  countyAverageScore: number;
  onDrill: (target: 'subregion', code: string) => void;
  /**
   * Optional controlled sort state. When `sortKey` + `sortDir` are provided
   * along with `onSortChange`, the table is fully controlled — column-header
   * clicks call `onSortChange` instead of touching local state. When omitted,
   * the table falls back to its internal `useState` defaults so existing
   * callers keep working.
   */
  sortKey?: SortKey;
  sortDir?: SortDir;
  onSortChange?: (key: SortKey, dir: SortDir) => void;
}

// ---------------------------------------------------------------------------
// Tier helpers — mirrors TierPill thresholds. Color is also conveyed by
// the score bar's fill class, so the table doesn't need a separate tier chip.
// ---------------------------------------------------------------------------
function tierColorClass(score: number): string {
  if (score >= 75) return 'bg-tier-anchor';
  if (score >= 60) return 'bg-tier-hot';
  if (score >= 40) return 'bg-tier-warm';
  return 'bg-tier-cool';
}

function tierLabel(score: number): string {
  if (score >= 75) return 'Anchor';
  if (score >= 60) return 'Hot';
  if (score >= 40) return 'Warm';
  return 'Cool';
}

// ---------------------------------------------------------------------------
// Column header cell — clickable, shows current sort direction.
// ---------------------------------------------------------------------------
function ColHeader({
  label,
  sortKey,
  currentKey,
  currentDir,
  onClick,
  align = 'right',
  width,
}: {
  label: string;
  sortKey?: SortKey;
  currentKey: SortKey;
  currentDir: SortDir;
  onClick?: (k: SortKey) => void;
  align?: 'left' | 'right';
  width?: string;
}) {
  const sortable = !!sortKey;
  const isActive = sortKey === currentKey;
  const Icon = !sortable
    ? null
    : !isActive
      ? ArrowUpDown
      : currentDir === 'desc'
        ? ArrowDown
        : ArrowUp;

  const content = (
    <span
      className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end w-full' : ''}`}
    >
      <span>{label}</span>
      {Icon && (
        <Icon
          className={`w-3 h-3 ${isActive ? 'text-accent-fg' : 'text-fg-muted/50'}`}
          aria-hidden="true"
        />
      )}
    </span>
  );

  if (!sortable) {
    return (
      <th
        scope="col"
        className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide text-fg-muted ${align === 'right' ? 'text-right' : 'text-left'}`}
        style={width ? { width } : undefined}
      >
        {content}
      </th>
    );
  }

  return (
    <th
      scope="col"
      className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide text-fg-muted ${align === 'right' ? 'text-right' : 'text-left'}`}
      style={width ? { width } : undefined}
      aria-sort={isActive ? (currentDir === 'desc' ? 'descending' : 'ascending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onClick?.(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-fg-primary transition-colors ${align === 'right' ? 'justify-end w-full' : ''}`}
      >
        {content}
      </button>
    </th>
  );
}

// ---------------------------------------------------------------------------
// Mini score bar — color = tier, length = score 0..100.
// Lives inside the Score cell so tier + score read as one unit.
// ---------------------------------------------------------------------------
function ScoreBar({ score }: { score: number }) {
  const pct = Math.max(0, Math.min(100, score));
  return (
    <div
      className="relative h-1.5 w-16 rounded-full bg-bg-elevated overflow-hidden"
      role="img"
      aria-label={`Score ${score.toFixed(1)} of 100, ${tierLabel(score)} tier`}
    >
      <div
        className={`absolute inset-y-0 left-0 ${tierColorClass(score)}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trend chip — shows delta vs the parent LD's average score.
// Compact: just an arrow + signed number.
// ---------------------------------------------------------------------------
function TrendChip({ delta }: { delta: number }) {
  if (Math.abs(delta) < 0.05) {
    return <span className="text-fg-muted text-xs tabular-nums">—</span>;
  }
  const isUp = delta > 0;
  const colorClass = isUp ? 'text-status-success' : 'text-status-danger';
  const arrow = isUp ? '↑' : '↓';
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums ${colorClass}`}>
      {arrow} {Math.abs(delta).toFixed(1)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main table component
// ---------------------------------------------------------------------------
export const SubregionTable: React.FC<Props> = ({
  subregions,
  ldCards,
  countyAverageScore,
  onDrill,
  sortKey: sortKeyProp,
  sortDir: sortDirProp,
  onSortChange,
}) => {
  const [sortKeyLocal, setSortKeyLocal] = useState<SortKey>('avg_score');
  const [sortDirLocal, setSortDirLocal] = useState<SortDir>('desc');

  const isControlled = sortKeyProp !== undefined && sortDirProp !== undefined;
  const sortKey: SortKey = isControlled ? (sortKeyProp as SortKey) : sortKeyLocal;
  const sortDir: SortDir = isControlled ? (sortDirProp as SortDir) : sortDirLocal;

  const handleSort = (k: SortKey) => {
    const nextDir: SortDir =
      k === sortKey ? (sortDir === 'desc' ? 'asc' : 'desc') : 'desc';
    if (!isControlled) {
      setSortKeyLocal(k);
      setSortDirLocal(nextDir);
    }
    onSortChange?.(k, nextDir);
  };

  // Group subregions by LD, then sort within each group.
  const grouped = useMemo(() => {
    const byLd: Record<number, SubregionRow[]> = {};
    for (const sr of subregions) {
      (byLd[sr.ld] ||= []).push(sr);
    }
    const lds = Object.keys(byLd)
      .map(Number)
      .sort((a, b) => a - b);

    return lds.map((ld) => {
      const rows = [...byLd[ld]].sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        const cmp = av === bv ? 0 : av < bv ? -1 : 1;
        return sortDir === 'desc' ? -cmp : cmp;
      });
      const ldCard = ldCards.find((l) => parseInt(l.district_key, 10) === ld);
      return {
        ld,
        ldName: LD_NAMES[ld] || `LD ${ld}`,
        ldTotal: ldCard?.total_voters ?? rows.reduce((s, r) => s + r.total, 0),
        ldAvgScore: ldCard?.avg_score ?? null,
        rows,
      };
    });
  }, [subregions, ldCards, sortKey, sortDir]);

  return (
    <div className="-mx-4 sm:mx-0 overflow-x-auto">
      <table className="w-full min-w-[760px] text-sm" data-testid="subregion-table">
        <thead className="sticky top-[calc(var(--app-header-h)+var(--subregion-controls-h,0px))] z-10 bg-bg-raised">
          <tr className="border-b border-border-base shadow-ds-sm">
            <ColHeader label="Subregion" currentKey={sortKey} currentDir={sortDir} align="left" />
            <ColHeader
              label="Voters"
              sortKey="total"
              currentKey={sortKey}
              currentDir={sortDir}
              onClick={handleSort}
              align="right"
            />
            <ColHeader
              label="Active %"
              sortKey="active_pct"
              currentKey={sortKey}
              currentDir={sortDir}
              onClick={handleSort}
              align="right"
            />
            <ColHeader
              label="Primary %"
              sortKey="primary_pct"
              currentKey={sortKey}
              currentDir={sortDir}
              onClick={handleSort}
              align="right"
            />
            <ColHeader
              label="Score"
              sortKey="avg_score"
              currentKey={sortKey}
              currentDir={sortDir}
              onClick={handleSort}
              align="right"
              width="180px"
            />
            <ColHeader
              label="vs LD"
              currentKey={sortKey}
              currentDir={sortDir}
              align="right"
              width="64px"
            />
            <th scope="col" className="px-2 py-2 w-8" aria-label="Drill into subregion" />
          </tr>
        </thead>
        <tbody>
          {grouped.map((group) => (
            <React.Fragment key={group.ld}>
              {/* LD section header — sits inline with the table, spans all cols.
                  Rendered as <th scope="colgroup"> rather than <td> because
                  position: sticky is unreliable on <tr>/<td> in browsers but
                  works on <th>. font-normal undoes the default <th> bold so
                  the inner styling fully controls weight. */}
              <tr>
                <th
                  colSpan={7}
                  scope="colgroup"
                  className="sticky top-[calc(var(--app-header-h)+var(--subregion-controls-h,0px))] z-10 bg-bg-elevated border-y border-border-base shadow-ds-sm px-3 py-2 text-left font-normal"
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs font-bold uppercase tracking-wider text-fg-secondary">
                      {group.ldName}
                      <span className="ml-2 font-normal normal-case tracking-normal text-fg-muted">
                        · {group.rows.length} subregion{group.rows.length === 1 ? '' : 's'}
                        {' · '}
                        <span className="tabular-nums">{group.ldTotal.toLocaleString()}</span> voters
                        {group.ldAvgScore !== null && (
                          <>
                            {' · LD avg score '}
                            <span className="tabular-nums">{group.ldAvgScore.toFixed(1)}</span>
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                </th>
              </tr>
              {group.rows.map((sr) => {
                const ldBaseline = group.ldAvgScore ?? countyAverageScore;
                const delta = sr.avg_score - ldBaseline;
                return (
                  <tr
                    key={sr.code}
                    onClick={() => onDrill('subregion', sr.code)}
                    className="border-b border-border-base/50 hover:bg-bg-elevated/30 cursor-pointer focus-within:bg-bg-elevated/30 transition-colors"
                    tabIndex={0}
                    role="button"
                    aria-label={`Drill into subregion ${sr.code} ${sr.name}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onDrill('subregion', sr.code);
                      }
                    }}
                  >
                    <td className="px-3 py-2.5">
                      <div className="font-mono text-xs text-fg-muted">{sr.code}</div>
                      <div className="font-medium text-fg-primary">{sr.name}</div>
                      <div className="text-xs text-fg-muted">
                        {sr.precinct_count} precinct{sr.precinct_count === 1 ? '' : 's'}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-fg-primary font-medium">
                      {sr.total.toLocaleString()}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-fg-secondary">
                      {sr.active_pct.toFixed(1)}%
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-fg-secondary">
                      {sr.primary_pct.toFixed(1)}%
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-2">
                        <span className="tabular-nums font-medium text-fg-primary">
                          {sr.avg_score.toFixed(1)}
                        </span>
                        <ScoreBar score={sr.avg_score} />
                      </div>
                      <div className="text-right text-[10px] uppercase tracking-wide text-fg-muted mt-0.5">
                        {tierLabel(sr.avg_score)}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <TrendChip delta={delta} />
                    </td>
                    <td className="px-2 py-2.5 text-fg-muted">
                      <ChevronRight className="w-4 h-4" aria-hidden="true" />
                    </td>
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      {grouped.length === 0 && (
        <div className="py-10 text-center text-fg-muted text-sm">
          No subregions to display.
        </div>
      )}
    </div>
  );
};

export default SubregionTable;
